import React from 'react';
import { Helmet } from 'react-helmet-async';
import { addLocalePrefix, getLocaleFromPathname } from '../i18n/routing';
import { isAdsEnabled } from '../utils/adConfig';

interface SEOMetaProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
  structuredData?: object;
  excludeAdsense?: boolean;
}

export const SEOMeta: React.FC<SEOMetaProps> = ({
  title,
  description,
  canonical,
  keywords,
  ogType = 'website',
  ogImage = 'https://el-impostor.com/android-chrome-512x512.png',
  article,
  structuredData,
  excludeAdsense = false,
}) => {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const locale = getLocaleFromPathname(pathname);

  // Asegurar que "El Impostor" esté siempre en el título para SEO
  const fullTitle = title.includes('Impostor') || title.includes('impostor') 
    ? title 
    : `${title} | El Impostor`;

  const localizedCanonical = (() => {
    if (!canonical) return undefined;
    if (typeof window === 'undefined') return canonical;

    try {
      const u = new URL(canonical, window.location.origin);
      if (u.origin === window.location.origin) {
        u.pathname = addLocalePrefix(u.pathname, locale);
      }
      return u.toString();
    } catch {
      // If canonical isn't a valid URL, leave it untouched.
      return canonical;
    }
  })();

  const url = (localizedCanonical || (typeof window !== 'undefined' ? window.location.href : ''));
  const ogLocale = locale === 'en' ? 'en_US' : locale === 'pt' ? 'pt_BR' : 'es_ES';
  const ogImageAlt =
    locale === 'en'
      ? 'El Impostor - Social Deduction Game'
      : locale === 'pt'
        ? 'El Impostor - Jogo de Dedução Social'
        : 'El Impostor - Juego de Deducción Social';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {localizedCanonical && <link rel="canonical" href={localizedCanonical} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      
      {/* Google AdSense - Excluido en páginas específicas como Privacy Policy y en entorno dev/local */}
      {!excludeAdsense && isAdsEnabled() && <meta name="google-adsense-account" content="ca-pub-2829473084602625" />}
      
      {/* Dominio y Marca */}
      <meta name="author" content="El Impostor - el-impostor.com" />
      <meta name="copyright" content="El Impostor" />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:site_name" content="El Impostor" />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* Article specific */}
      {article && ogType === 'article' && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && <meta property="article:author" content={article.author} />}
        </>
      )}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};
